import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Reveal } from "@/components/site/Reveal";
import { FEATURED_DEALS } from "@/lib/content";

const Stars = ({ rating, className = "h-4 w-4" }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${className} ${i <= rating ? "fill-[#F8B808] text-[#F8B808]" : "fill-[#E3E0D9] text-[#E3E0D9]"}`}
      />
    ))}
  </span>
);

const useVisibleCount = () => {
  const [n, setN] = useState(5);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      if (w >= 1024) setN(5);
      else if (w >= 768) setN(3);
      else if (w >= 560) setN(2);
      else setN(1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return n;
};

export const FeaturedDeals = () => {
  const visible = useVisibleCount();
  const n = FEATURED_DEALS.length;
  const [index, setIndex] = useState(visible);
  const [anim, setAnim] = useState(false);
  const [cardW, setCardW] = useState(0);
  const [active, setActive] = useState(null);
  const trackRef = useRef(null);

  // Extended list with cloned items on both ends for seamless infinite looping
  const ext = [
    ...FEATURED_DEALS.slice(n - visible).map((p, k) => ({ p, orig: n - visible + k })),
    ...FEATURED_DEALS.map((p, k) => ({ p, orig: k })),
    ...FEATURED_DEALS.slice(0, visible).map((p, k) => ({ p, orig: k })),
  ];

  useLayoutEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        setCardW(trackRef.current.offsetWidth / visible);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [visible]);

  // Reset to the first real slide when the visible count changes
  useEffect(() => {
    setAnim(false);
    setIndex(visible);
  }, [visible]);

  // Re-enable the transition after a silent (non-animated) wrap jump
  useEffect(() => {
    if (!anim) {
      const id = requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnim(true))
      );
      return () => cancelAnimationFrame(id);
    }
  }, [anim, index]);

  const prev = () => anim && setIndex((i) => i - 1);
  const next = () => anim && setIndex((i) => i + 1);

  const handleEnd = () => {
    if (index >= n + visible) {
      setAnim(false);
      setIndex((i) => i - n);
    } else if (index < visible) {
      setAnim(false);
      setIndex((i) => i + n);
    }
  };

  const openContact = () => {
    setActive(null);
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  return (
    <section className="py-[35px] lg:py-[50px] bg-[#F1F1F3]" data-testid="featured-deals-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6">
          <Reveal>
            <div>
              <h2 className="font-heading text-[31px] sm:text-5xl lg:text-[50px] font-medium tracking-tight text-[#1A1A1A] leading-[1.04]">
                Best Sellers
              </h2>
            </div>
          </Reveal>
        </div>

        <div className="relative mt-[34px] lg:mt-14">
          <button
            onClick={prev}
            data-testid="deals-prev"
            aria-label="Previous deals"
            className="flex absolute left-1 sm:left-0 top-1/2 -translate-y-1/2 sm:-translate-x-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-[#1A1A1A]"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            data-testid="deals-next"
            aria-label="Next deals"
            className="flex absolute right-1 sm:right-0 top-1/2 -translate-y-1/2 sm:translate-x-1/2 z-20 h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-[#1A1A1A]"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="overflow-hidden" ref={trackRef}>
          <div
            className={`flex ${anim ? "transition-transform duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]" : ""}`}
            style={{ transform: `translateX(-${index * cardW}px)` }}
            onTransitionEnd={handleEnd}
          >
            {ext.map((item, k) => {
              const p = item.p;
              const i = item.orig;
              return (
              <div
                key={k}
                data-testid={`deal-card-${i}`}
                className="shrink-0 px-2.5"
                style={{ width: cardW ? `${cardW}px` : `${100 / visible}%` }}
              >
                <div className="group flex h-full flex-col rounded-[8px] bg-white p-4">
                  <div className="relative flex aspect-[1/1] w-full items-center justify-center p-1">
                    {p.save && (
                      <div className="absolute top-2 left-2 z-10">
                        <span
                          className="inline-block rounded-[3px] bg-[#F8B808] pl-2 pr-3 py-1 font-body text-[10px] font-bold uppercase tracking-wide text-[#1A1A1A]"
                          style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 8px) 100%, 0 100%)" }}
                        >
                          {p.save}
                        </span>
                      </div>
                    )}
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="h-full w-full object-contain [object-position:50%_42%]"
                      style={{ transform: `scale(${({1: 1.1, 2: 1.39, 3: 1.68, 9: 1.68})[i] || 1.1})` }}
                    />
                  </div>
                  <p className="font-body text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9A958C] mt-3.5">
                    {p.category}
                  </p>
                  <h3 className="font-body text-sm font-normal text-[#1A1A1A] mt-1.5 leading-relaxed">
                    {p.title}
                  </h3>
                  <div className="mb-5">
                    <div className="flex items-center gap-2 mt-2">
                      <Stars rating={p.rating} className="h-3.5 w-3.5" />
                      <span className="font-body text-xs text-[#9A958C]">({p.reviews})</span>
                    </div>
                    {p.price && (
                      <div className="mt-3">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          {p.originalPrice && (
                            <span className="font-body text-sm text-[#9A958C] line-through">{p.originalPrice}</span>
                          )}
                          <span className="font-heading text-lg font-bold text-[#1A1A1A]">{p.price}</span>
                        </div>
                        {p.savings && (
                          <p className="mt-1 font-body text-xs font-semibold text-[#F58220]">{p.savings}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setActive(p)}
                    data-testid={`deal-details-${i}`}
                    className="mt-auto inline-flex items-center justify-center rounded-[5px] bg-[#F8B808] px-4 h-10 font-body text-sm font-bold text-[#1A1A1A] hover:bg-[#E5A800]"
                  >
                    See Details
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden rounded-[10px] border-none">
          {active && (
            <div className="grid md:grid-cols-2" data-testid="deal-modal">
              <div className="flex items-center justify-center bg-[#F7F7F8] p-10 min-h-[280px]">
                <img
                  src={active.image}
                  alt={active.title}
                  className="max-h-[300px] w-auto object-contain"
                />
              </div>
              <div className="p-8 lg:p-10">
                <p className="font-body text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9A958C]">
                  {active.category}
                </p>
                <DialogTitle className="font-heading text-2xl lg:text-3xl font-bold text-[#1A1A1A] mt-1.5 leading-tight">
                  {active.title}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-3">
                  <Stars rating={active.rating} />
                  <span className="font-body text-xs text-[#9A958C]">({active.reviews} reviews)</span>
                </div>
                <p className="font-body text-sm text-[#6B6B66] mt-4 leading-relaxed">
                  {active.description}
                </p>
                <DialogDescription className="sr-only">
                  {active.title} — {active.description}
                </DialogDescription>
                <ul className="mt-5 space-y-2">
                  {[
                    "Trusted national brands",
                    "Fully inspected",
                    "Inventory changes regularly",
                    "Significant savings compared to traditional retail",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 font-body text-sm text-[#1A1A1A]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F8B808]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 rounded-xl bg-[#FBF4DD] p-4">
                  <p className="font-body text-xs font-semibold uppercase tracking-wide text-[#9A6A00]">
                    Typical Savings
                  </p>
                  <p className="font-heading text-lg font-bold text-[#1A1A1A] mt-1">
                    Up to 60% below traditional retail prices.
                  </p>
                </div>
                <p className="font-body text-[11px] text-[#9A958C] mt-4 leading-relaxed">
                  Products shown are representative. Inventory and availability vary by location.
                </p>
                <button
                  onClick={openContact}
                  data-testid="deal-request-catalog"
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-[5px] bg-[#F8B808] px-6 h-10 font-body text-base font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98]"
                >
                  Request Catalog
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
